import { useState } from "react";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Check, Copy, Link, Mail } from "lucide-react";
import { toast } from "sonner";
import { AppDialog } from "@/components/common/AppDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

type ShareTab = "link" | "email";
type AccessLevel = "view" | "edit";
type LinkDuration = "7" | "14" | "30" | "90";

interface SharedUser {
  id: string;
  email: string;
  avatarUrl?: string;
  status: "active" | "expired";
  expiresInDays?: number;
}

const sharedUsers: SharedUser[] = [];

const catalogShareUrl =
  typeof window !== "undefined"
    ? `${window.location.origin}/catalog`
    : "/catalog";

function initials(email: string) {
  const parts = (email.split("@")[0] ?? email).split(/[._-]/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

interface ShareRecipesCatalogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareRecipesCatalogDialog({
  open,
  onOpenChange,
}: ShareRecipesCatalogDialogProps) {
  const { i18n } = useLingui();
  const [tab, setTab] = useState<ShareTab>("link");
  const [access, setAccess] = useState<AccessLevel>("view");
  const [duration, setDuration] = useState<LinkDuration>("7");
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(catalogShareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendEmail = () => {
    if (!email.trim()) return;
    toast.success(i18n._("Invite sent"));
    setEmail("");
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._("Share Recipe Catalog")}
      description={i18n._("Share a read-only link to your recipe catalog. Recipients can view recipes without needing an account.")}
      size="lg"
    >
      <div className="space-y-5">
        {/* Tab switcher */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as ShareTab)}>
          <TabsList className="w-full">
            <TabsTrigger value="link" className="flex-1">
              {i18n._("Link")}
            </TabsTrigger>
            <TabsTrigger value="email" className="flex-1">
              {i18n._("Email")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Link tab */}
        {tab === "link" && (
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block text-sm font-medium">{i18n._("Share via link")}</Label>
              <div className="flex items-center gap-2 rounded-lg border bg-(--color-surface-muted)/40 px-3 py-2 text-sm">
                <Link className="size-4 shrink-0 text-(--color-text-secondary)" />
                <span className="flex-1 truncate text-(--color-text-secondary)">{catalogShareUrl}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                  aria-label={i18n._("Copy link")}
                  className="ml-1 size-auto shrink-0 text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary) [&_svg]:size-4"
                >
                  {copied ? <Check /> : <Copy />}
                </Button>
              </div>
            </div>
            <AccessDurationRow
              access={access}
              duration={duration}
              onAccessChange={setAccess}
              onDurationChange={setDuration}
            />
          </div>
        )}

        {/* Email tab */}
        {tab === "email" && (
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block text-sm font-medium">{i18n._("Share via email")}</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={i18n._("Enter email address...")}
                    className="pl-9"
                    onKeyDown={(e) => e.key === "Enter" && handleSendEmail()}
                  />
                </div>
                <Button onClick={handleSendEmail} disabled={!email.trim()}>
                  {i18n._("Send")}
                </Button>
              </div>
            </div>
            <AccessDurationRow
              access={access}
              duration={duration}
              onAccessChange={setAccess}
              onDurationChange={setDuration}
            />
          </div>
        )}

        {/* Shared with list */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{i18n._("Shared with")}</span>
            <Badge variant="secondary" className="text-xs">
              {sharedUsers.length}
            </Badge>
          </div>
          {sharedUsers.length === 0 ? (
            <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-(--color-text-secondary)">
              {i18n._("No recipients yet. Send a link or invite someone by email.")}
            </p>
          ) : (
            <div className="space-y-2">
              {sharedUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between rounded-lg border px-3 py-2.5"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="size-8">
                      {user.avatarUrl && <AvatarImage src={user.avatarUrl} />}
                      <AvatarFallback className="text-xs">{initials(user.email)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{user.email}</span>
                        <Badge
                          variant={user.status === "active" ? "default" : "secondary"}
                          className={
                            user.status === "active"
                              ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)"
                              : ""
                          }
                        >
                          {user.status === "active" ? i18n._("Active") : i18n._("Expired")}
                        </Badge>
                      </div>
                      <p className="text-xs text-(--color-text-secondary)">
                        {user.status === "active" && user.expiresInDays
                          ? i18n._(t`Expiring in ${user.expiresInDays} days`)
                          : i18n._("Link is expired")}
                      </p>
                    </div>
                  </div>
                  <Select defaultValue="view">
                    <SelectTrigger className="h-8 w-28 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">{i18n._("View only")}</SelectItem>
                      <SelectItem value="edit">{i18n._("Can edit")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppDialog>
  );
}

function AccessDurationRow({
  access,
  duration,
  onAccessChange,
  onDurationChange,
}: {
  access: AccessLevel;
  duration: LinkDuration;
  onAccessChange: (v: AccessLevel) => void;
  onDurationChange: (v: LinkDuration) => void;
}) {
  const { i18n } = useLingui();

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-sm">{i18n._("Access")}</Label>
        <Select value={access} onValueChange={(v) => onAccessChange(v as AccessLevel)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="view">{i18n._("View only")}</SelectItem>
            <SelectItem value="edit">{i18n._("Can edit")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm">{i18n._("Link Duration")}</Label>
        <Select value={duration} onValueChange={(v) => onDurationChange(v as LinkDuration)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{i18n._("7 days")}</SelectItem>
            <SelectItem value="14">{i18n._("14 days")}</SelectItem>
            <SelectItem value="30">{i18n._("30 days")}</SelectItem>
            <SelectItem value="90">{i18n._("90 days")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
